import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';

type UpdateLineItemInput = Parameters<
  typeof rpcClient.accountsReceivable.draftSalesOrders.updateLineItem
>[0];
type UpdateHeaderInput = Parameters<
  typeof rpcClient.accountsReceivable.draftSalesOrders.updateHeader
>[0];
type AddLineItemInput = Parameters<
  typeof rpcClient.accountsReceivable.draftSalesOrders.addLineItem
>[0];

/**
 * Inventory-availability check for a v3 draft's lines. POST but read-mostly
 * (it queries the ERP and persists each line's inventoryStatus), so it's
 * modeled as a query — mirrors the legacy staged-PO hook. Results are keyed
 * by productVariantId / customerProductId.
 */
export function useValidateDraftSalesOrderInventory(
  draftSalesOrderId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: orpc.accountsReceivable.draftSalesOrders.validateInventory.key({
      input: { draftSalesOrderId },
    }),
    queryFn: async () =>
      rpcClient.accountsReceivable.draftSalesOrders.validateInventory({
        draftSalesOrderId,
      }),
    enabled: enabled && !!draftSalesOrderId,
    // Potentially slow ERP call — don't refetch on focus, cache for a minute.
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
}

/** Fetch a relational draft sales order + its ordered line items. */
export function useDraftSalesOrder(id: string | undefined) {
  const query = useQuery({
    ...orpc.accountsReceivable.draftSalesOrders.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: !!id,
  });
  return { ...query, draftSalesOrder: query.data?.draftSalesOrder };
}

/** Update a single draft line item by its stable id, then refetch the draft. */
export function useUpdateDraftSalesOrderLineItem(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<UpdateLineItemInput, 'draftSalesOrderId'>) =>
      rpcClient.accountsReceivable.draftSalesOrders.updateLineItem({
        draftSalesOrderId,
        ...input,
      }),
    onSuccess: () => {
      // Refetch wherever the draft is shown — the v3 review page reads
      // salesOrdersV3.get, the legacy v2 modal reads draftSalesOrders.get.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      // A quantity/price/warehouse edit moves the stamped facts, discount
      // validity, inventory sufficiency, and writes activity — refresh them
      // all (same set the match mutation invalidates).
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateDiscounts.key({
            input: { draftSalesOrderId },
          }),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateInventory.key({
            input: { draftSalesOrderId },
          }),
      });
      // A unit-price edit drops the line's Discounts v2 stack server-side —
      // refresh the stacks + per-line discount states so the chip flips.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.discountsV2.getDraftLineStacks.key({
          input: { draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.discountsV2.validateDraftDiscounts.key({
            input: { draftSalesOrderId },
          }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
          input: { draftSalesOrderId },
        }),
      });
    },
  });
}

/**
 * Match a draft line to a product variant (the review-page "Match" column).
 * Beyond a plain line update, the backend also upserts the CustomerProduct
 * mapping so future orders from the customer auto-match.
 */
export function useMatchDraftSalesOrderLineItem(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      lineItemId: string;
      productVariantId: string;
    }) =>
      rpcClient.accountsReceivable.draftSalesOrders.matchLineItem({
        draftSalesOrderId,
        ...input,
      }),
    onSuccess: () => {
      // Refetch wherever the draft is shown (v3 review page + legacy v2 modal).
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      // A product change resets the line's discounts and re-keys the
      // inventory check on the new variant.
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateDiscounts.key({
            input: { draftSalesOrderId },
          }),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateInventory.key({
            input: { draftSalesOrderId },
          }),
      });
      // The edit invalidates approvals / writes activity — refresh the timeline.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
          input: { draftSalesOrderId },
        }),
      });
    },
  });
}

/** Append a manually-added line item to a draft, then refetch the draft. */
export function useAddDraftSalesOrderLineItem(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<AddLineItemInput, 'draftSalesOrderId'>) =>
      rpcClient.accountsReceivable.draftSalesOrders.addLineItem({
        draftSalesOrderId,
        ...input,
      }),
    onSuccess: () => {
      // Refetch wherever the draft is shown (v3 review page + legacy v2 modal),
      // plus the per-line discount validation for the new line.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateDiscounts.key({
            input: { draftSalesOrderId },
          }),
      });
      // The add/remove writes an activity entry — refresh the timeline.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
          input: { draftSalesOrderId },
        }),
      });
    },
  });
}

/** Delete a draft line item by its id, then refetch the draft. */
export function useDeleteDraftSalesOrderLineItem(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lineItemId: string) =>
      rpcClient.accountsReceivable.draftSalesOrders.deleteLineItem({
        draftSalesOrderId,
        lineItemId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.draftSalesOrders.validateDiscounts.key({
            input: { draftSalesOrderId },
          }),
      });
      // The add/remove writes an activity entry — refresh the timeline.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
          input: { draftSalesOrderId },
        }),
      });
    },
  });
}

/** Update a draft sales order's header fields, then refetch the draft. */
export function useUpdateDraftSalesOrderHeader(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<UpdateHeaderInput, 'draftSalesOrderId'>) =>
      rpcClient.accountsReceivable.draftSalesOrders.updateHeader({
        draftSalesOrderId,
        ...input,
      }),
    onSuccess: () => {
      // Two readers consume this draft: the legacy v2 modal (draftSalesOrders.get)
      // and the OMS v3 review page (salesOrdersV3.get). Invalidate both so the
      // edit (and any re-derived fields) refetch wherever the draft is shown.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftSalesOrderId },
        }),
      });
      // The edit is recorded in the draft activity log — refresh the timeline.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
          input: { draftSalesOrderId },
        }),
      });
    },
  });
}
